import React from 'react';
import type { AgentRequest, PatientDoc } from '@usacsmideo/types';
import { Button } from '../buttons';
import usePatientDoc from '../../hooks/usePatientDoc';
import { useUpdateUserDoc } from '../../hooks/useUpdateUserDoc';
import { useRelationshipsWhereImAgent } from '../../hooks/useAgentRequests';
import { archiveAgentRequest } from '../../data/agents/handleAgentRequest';

/**
 * ECN Subtask 9 (CU-86ahjxrgg) — agent-only view of My MIDEO Family (redesign §3.1).
 *
 * The agent-only persona's ONE self-service surface beyond /sessions. They:
 *   - see the patients they are an accepted agent of,
 *   - edit their OWN contact details (core PatientDoc fields — the same doc that
 *     flips to a patient chart if they ever "Become a patient"; labeled honestly
 *     as "Your contact details", not "patient profile"),
 *   - "Step down" from a relationship (archives the AgentRequest where they are the
 *     recipient — the backend authorizes the recipient and records archivedBy).
 *
 * Reads NO chart data — safe for a chart-less agent.
 */

/** Patient (sender) display name, denormalized on the request; neutral fallback
 *  for requests created before senderName existed. */
function patientName(request: AgentRequest): string {
  return request.senderName?.trim() || 'A patient you support';
}

function roleLabel(request: AgentRequest): string {
  // "the" Healthcare Agent (one per patient) vs "an" Alternate Agent (up to two).
  return request.agentType === 'HCA'
    ? 'You are the healthcare agent'
    : 'You are an alternate agent';
}

function RelationshipRow({
  request,
  onStepDown,
  steppingDown,
}: {
  request: AgentRequest;
  onStepDown: (id: string) => void;
  steppingDown: boolean;
}) {
  return (
    <div className="flex w-full items-center justify-between rounded-md border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-left">
        <p className="font-semibold text-sky-800">{patientName(request)}</p>
        {request.senderEmail && (
          <p className="text-sm text-gray-500">{request.senderEmail}</p>
        )}
        <p className="text-sm text-gray-600">{roleLabel(request)}</p>
      </div>
      <Button
        type="button"
        id={`stepdown-${request.id}`}
        textContent="Step down"
        working={steppingDown}
        workingText="Updating…"
        disabled={steppingDown}
        classNameOverride="my-4 rounded-md border border-[#730D0E] px-4 py-2 text-xl text-[#730D0E] hover:bg-[#730D0E]/10 sm:px-8 sm:py-3"
        onClick={() => onStepDown(request.id)}
      />
    </div>
  );
}

export function AgentFamilyView({ agentUid }: { agentUid: string }) {
  const { accepted, hasResolved } = useRelationshipsWhereImAgent();
  const patientDocQuery = usePatientDoc(agentUid);
  const doc = patientDocQuery.data as PatientDoc | undefined;
  const { mutate } = useUpdateUserDoc();

  const [displayName, setDisplayName] = React.useState('');
  const [seeded, setSeeded] = React.useState(false);
  const [savingContact, setSavingContact] = React.useState(false);
  const [contactSaved, setContactSaved] = React.useState(false);
  const [steppingDownId, setSteppingDownId] = React.useState<string | null>(null);
  const [confirmId, setConfirmId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Seed the form once from the loaded doc.
  React.useEffect(() => {
    if (doc && !seeded) {
      setDisplayName(doc.displayName ?? '');
      setSeeded(true);
    }
  }, [doc, seeded]);

  // Name-only edit. Agent reachability is push (fcmTokens) + SMS (ghlContactId,
  // set at accept) — NOT email — so the form deliberately edits only the name an
  // agent shows to patients. accountEmail (login identity) and the legacy
  // contactEmail are intentionally NOT exposed here. Single write → no race.
  const handleSaveContact = () => {
    setSavingContact(true);
    setContactSaved(false);
    setError(null);
    mutate(
      { patientId: agentUid, displayName },
      {
        onSuccess: () => {
          setSavingContact(false);
          setContactSaved(true);
        },
        onError: (e: Error) => {
          setSavingContact(false);
          setError('We could not save your name. Please try again.');
          console.error(e);
        },
      },
    );
  };

  const handleStepDown = async (id: string) => {
    setSteppingDownId(id);
    setConfirmId(null);
    setError(null);
    try {
      await archiveAgentRequest(id);
    } catch (e) {
      setError('We could not update this relationship. Please try again or contact support.');
    } finally {
      setSteppingDownId(null);
    }
  };

  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-sky-800">My MIDEO Family</h1>
        <p className="mt-1 text-gray-600">
          Manage the patients you support as a MIDEO agent.
        </p>
      </div>

      {error && (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {/* Your name — the only agent-editable field. Reachability (push/SMS) is set
          at acceptance and routed via FCM/GHL, not via an email here. */}
      <section className="flex flex-col gap-3 rounded-md border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800">Your name</h2>
        <p className="text-sm text-gray-600">
          This is the name patients see for you.
        </p>
        <label className="flex flex-col text-left text-sm text-gray-700">
          Name
          <input
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setContactSaved(false);
            }}
            className="mt-1 rounded-md border border-gray-300 px-3 py-2"
          />
        </label>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            id="save-contact"
            textContent="Save"
            working={savingContact}
            workingText="Saving…"
            disabled={savingContact}
            onClick={handleSaveContact}
          />
          {contactSaved && <span className="text-sm text-green-700">Saved.</span>}
        </div>
      </section>

      {/* Patients I'm an agent for. */}
      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-gray-800">Patients you support</h2>
        {!hasResolved ? (
          <p className="text-gray-600">Loading…</p>
        ) : accepted.length === 0 ? (
          <p className="text-gray-600">
            You are not currently an active agent for anyone.
          </p>
        ) : (
          accepted.map((r) =>
            confirmId === r.id ? (
              <div
                key={r.id}
                className="flex flex-col gap-3 rounded-md border border-[#730D0E]/30 bg-red-50 p-4"
              >
                <p className="text-left text-sm text-gray-800">
                  Stepping down ends your authorization for {patientName(r)}. You will stop receiving
                  their emergency notifications and will no longer have access. This cannot be undone
                  from here — they would need to re-invite you.
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    id={`confirm-stepdown-${r.id}`}
                    textContent="Yes, step down"
                    working={steppingDownId === r.id}
                    workingText="Updating…"
                    disabled={steppingDownId === r.id}
                    onClick={() => handleStepDown(r.id)}
                  />
                  <Button
                    type="button"
                    id={`cancel-stepdown-${r.id}`}
                    textContent="Cancel"
                    classNameOverride="my-4 rounded-md border border-gray-300 px-4 py-2 text-xl text-gray-700 hover:bg-gray-100 sm:px-8 sm:py-3"
                    onClick={() => setConfirmId(null)}
                  />
                </div>
              </div>
            ) : (
              <RelationshipRow
                key={r.id}
                request={r}
                steppingDown={steppingDownId === r.id}
                onStepDown={() => setConfirmId(r.id)}
              />
            ),
          )
        )}
      </section>
    </div>
  );
}

export default AgentFamilyView;
